import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'

/** Breakpoint below which TOC auto-collapses on mount (portrait mobile) */
const MOBILE_BREAKPOINT = 768

export type SidebarUIContextValue = {
  // State
  tocCollapsed: boolean
  profileOpen: boolean
  transitions: boolean
  // Actions
  setTocCollapsed: (collapsed: boolean) => void
  toggleToc: () => void
  openProfile: () => void
  closeProfile: () => void
  toggleProfile: () => void
  setTransitions: (value: boolean) => void
}

const SidebarUIContext = createContext<SidebarUIContextValue | undefined>(
  undefined
)
SidebarUIContext.displayName = 'SidebarUIContext'

export type SidebarUIProviderProps = {
  children: ReactNode
  /** Initial collapsed state of TOC; defaults to false (expanded) */
  initialTocCollapsed?: boolean
  /** Initial transitions enabled for react-sidebar; defaults to true */
  initialTransitions?: boolean
  /**
   * When opening Profile, collapse TOC and remember its previous value.
   * When closing Profile, restore remembered TOC value.
   * Defaults to false.
   */
  rememberAndCollapseTocOnProfile?: boolean
}

export const SidebarUIProvider: React.FC<SidebarUIProviderProps> = ({
  children,
  initialTocCollapsed = false,
  initialTransitions = true,
  rememberAndCollapseTocOnProfile = false,
}) => {
  const [tocCollapsed, setTocCollapsedState] = useState<boolean>(
    Boolean(initialTocCollapsed)
  )
  const [profileOpen, setProfileOpen] = useState<boolean>(false)
  const [transitions, setTransitions] = useState<boolean>(
    Boolean(initialTransitions)
  )

  // Holds the last known TOC state to restore after closing Profile
  const prevTocCollapsedRef = useRef<boolean | null>(null)

  // Auto-collapse TOC on mobile/portrait devices for better map usability
  useEffect(() => {
    const isMobileViewport = window.innerWidth < MOBILE_BREAKPOINT
    if (isMobileViewport) {
      setTocCollapsedState(true)
    }
  }, [])

  const setTocCollapsed = useCallback((collapsed: boolean) => {
    setTocCollapsedState(Boolean(collapsed))
  }, [])

  const toggleToc = useCallback(() => {
    setTocCollapsedState(prev => !prev)
  }, [])

  const openProfile = useCallback(() => {
    // If TOC is open, collapse it and remember; if already collapsed, leave as-is
    if (rememberAndCollapseTocOnProfile) {
      if (prevTocCollapsedRef.current == null) {
        prevTocCollapsedRef.current = tocCollapsed
      }
      if (!tocCollapsed) setTocCollapsedState(true)
    }
    setProfileOpen(true)
  }, [rememberAndCollapseTocOnProfile, tocCollapsed])

  const closeProfile = useCallback(() => {
    setProfileOpen(false)
    if (
      rememberAndCollapseTocOnProfile &&
      prevTocCollapsedRef.current != null
    ) {
      setTocCollapsedState(prevTocCollapsedRef.current)
      prevTocCollapsedRef.current = null
    }
  }, [rememberAndCollapseTocOnProfile])

  const toggleProfile = useCallback(() => {
    if (profileOpen) {
      closeProfile()
    } else {
      openProfile()
    }
  }, [closeProfile, openProfile, profileOpen])

  const value = useMemo<SidebarUIContextValue>(
    () => ({
      tocCollapsed,
      profileOpen,
      transitions,
      setTocCollapsed,
      toggleToc,
      openProfile,
      closeProfile,
      toggleProfile,
      setTransitions,
    }),
    [
      tocCollapsed,
      profileOpen,
      transitions,
      setTocCollapsed,
      toggleToc,
      openProfile,
      closeProfile,
      toggleProfile,
      setTransitions,
    ]
  )

  return (
    <SidebarUIContext.Provider value={value}>
      {children}
    </SidebarUIContext.Provider>
  )
}

export const useSidebarUI = (): SidebarUIContextValue => {
  const ctx = useContext(SidebarUIContext)
  if (ctx === undefined) {
    throw new Error('useSidebarUI must be used within a SidebarUIProvider')
  }
  return ctx
}
